// Catalogue 1.0 requests (city_os#83): how an area made on the catalogue maps reaches /data-agg. Admin areas go as
// they are saved (geo_type admin_lvl_<n>, ids NO_2_0301). A model-grid area holds cells of several sizes, saved as
// size-tagged keys ('grid_500m:22610006649000'); the API takes one geo type per request and plain square ids, so
// Activities asks once per cell size, and Origin-Destination reads every cell at one size (the adaptive data only
// pairs cells of one size). Rows come back keyed by the plain id plus their geo_type, and are keyed again here the
// way the area saves them, so the pages match rows to cells. Pure: no React, router or network.
import { MODEL_GRID, gridCellKey, gridParentId, gridSizeM, parseGridCellKey } from '../config/catalogue';

/** The model grid's cell sizes, finest first, as the geo types /data-agg takes. */
export const GRID_RUNGS: readonly string[] = ['grid_250m', 'grid_500m', 'grid_1000m', 'grid_5000m', 'grid_10000m'];

/** The ids of a model-grid area, grouped by cell size (geo type), in size order. Ids that are not cell keys are left out. */
export function idsBySize(ids: readonly (string | number)[]): Array<{ geoType: string; ids: string[] }> {
  const groups = new Map<string, Set<string>>();
  for (const raw of ids) {
    const key = parseGridCellKey(String(raw));
    if (!key) continue;
    (groups.get(key.geoType) ?? groups.set(key.geoType, new Set()).get(key.geoType)!).add(key.geoId);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (gridSizeM(a) ?? 0) - (gridSizeM(b) ?? 0))
    .map(([geoType, set]) => ({ geoType, ids: Array.from(set) }));
}

/** The largest cell size in a model-grid area, as a geo type; null for none. */
export function largestRung(ids: readonly (string | number)[]): string | null {
  const sizes = idsBySize(ids);
  return sizes.length ? sizes[sizes.length - 1].geoType : null;
}

/** The sizes an area can be read at for Origin-Destination: its largest cell size and every coarser one. */
export function rungsFor(ids: readonly (string | number)[]): string[] {
  const largest = gridSizeM(largestRung(ids) ?? '') ?? 0;
  return GRID_RUNGS.filter((g) => (gridSizeM(g) ?? 0) >= largest);
}

/**
 * The cells of a model-grid area at one size: each cell as the square of `rung` that holds it. A cell larger than
 * the rung cannot be read at it and is counted in `dropped`; a smaller one stands as its whole parent square
 * (`widened`), so the ground read can be larger than the area.
 */
export function cellsAtRung(
  ids: readonly (string | number)[],
  rung: string,
): { ids: string[]; widened: number; dropped: number } {
  const toM = gridSizeM(rung);
  const out = new Set<string>();
  let widened = 0;
  let dropped = 0;
  if (toM === null) return { ids: [], widened, dropped: ids.length };
  for (const group of idsBySize(ids)) {
    const fromM = gridSizeM(group.geoType) ?? 0;
    if (fromM > toM) {
      dropped += group.ids.length;
      continue;
    }
    for (const id of group.ids) {
      const parent = fromM === toM ? id : gridParentId(id, fromM, toM);
      if (parent === null) {
        dropped += 1;
        continue;
      }
      if (fromM < toM) widened += 1;
      out.add(parent);
    }
  }
  return { ids: Array.from(out).sort(), widened, dropped };
}

/** True for a grid geo type of the catalogue (grid_250m ... grid_10000m). */
const isGridType = (v: unknown): v is string => typeof v === 'string' && /^grid_\d+m$/.test(v);

/** A row's area id as the saved area names it: a grid cell's key ('grid_1000m:<id>'), an admin area's own id. */
export function rowAreaKey(id: unknown, geoType: unknown): string {
  const plain = id == null ? '' : String(id);
  return isGridType(geoType) && plain !== '' ? gridCellKey(geoType, plain) : plain;
}

/** True when the area type is the catalogue's model grid. */
export const isModelGrid = (geoType: string | null | undefined): boolean => geoType === MODEL_GRID;
